import { useState, useEffect, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const COMMIT_THRESHOLD_RATIO = 0.18; // 18% of axis size triggers commit
const COMMIT_VELOCITY = 0.5;          // px/ms — fast flick also triggers commit
const ANIM_MS = 220;
const AXIS_LOCK_PX = 8;

/**
 * Full-screen image carousel with TikTok-style drag-and-snap navigation.
 *
 * Drag horizontally or vertically; the current image follows your finger
 * and the prev/next image is stacked just offscreen so it slides in.
 * Cross the threshold or flick fast and the navigation commits with an
 * animation; release short and it snaps back.
 */
export default function ImageViewer({ items, startIndex = 0, onClose, onDelete }) {
  const [index, setIndex] = useState(startIndex);
  const [drag, setDrag] = useState({ dx: 0, dy: 0 });
  const [animState, setAnimState] = useState("idle"); // idle | dragging | snapping | committing
  const startRef = useRef(null);   // { x, y, t }
  const axisRef = useRef(null);    // 'x' | 'y' | null
  const containerRef = useRef(null);

  const item = items[index];
  const prevItem = items[(index - 1 + items.length) % items.length];
  const nextItem = items[(index + 1) % items.length];

  useEffect(() => {
    setIndex(Math.min(startIndex, items.length - 1));
  }, [startIndex, items.length]);

  const goPrev = useCallback(
    () => setIndex((i) => (i > 0 ? i - 1 : items.length - 1)),
    [items.length]
  );
  const goNext = useCallback(
    () => setIndex((i) => (i < items.length - 1 ? i + 1 : 0)),
    [items.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const getSize = () => {
    const el = containerRef.current;
    return {
      w: el?.clientWidth ?? window.innerWidth,
      h: el?.clientHeight ?? window.innerHeight,
    };
  };

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    startRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    axisRef.current = null;
    setAnimState("dragging");
  };

  const handleTouchMove = (e) => {
    if (!startRef.current) return;
    const t = e.touches[0];
    const dx = t.clientX - startRef.current.x;
    const dy = t.clientY - startRef.current.y;
    if (!axisRef.current) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      axisRef.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    }
    if (axisRef.current === "x") setDrag({ dx, dy: 0 });
    else setDrag({ dx: 0, dy });
  };

  const handleTouchEnd = () => {
    const start = startRef.current;
    const axis = axisRef.current;
    startRef.current = null;
    axisRef.current = null;
    if (!start || !axis) {
      setAnimState("idle");
      setDrag({ dx: 0, dy: 0 });
      return;
    }
    const { w, h } = getSize();
    const elapsed = Math.max(1, Date.now() - start.t);
    const value = axis === "x" ? drag.dx : drag.dy;
    const velocity = Math.abs(value) / elapsed; // px/ms
    const threshold = (axis === "x" ? w : h) * COMMIT_THRESHOLD_RATIO;
    const shouldCommit =
      Math.abs(value) > threshold || velocity > COMMIT_VELOCITY;

    if (!shouldCommit) {
      // Snap back
      setAnimState("snapping");
      setDrag({ dx: 0, dy: 0 });
      setTimeout(() => setAnimState("idle"), ANIM_MS);
      return;
    }

    // Commit: animate current image fully off in drag direction.
    const dir = value > 0 ? 1 : -1;
    setAnimState("committing");
    setDrag(axis === "x" ? { dx: dir * w, dy: 0 } : { dx: 0, dy: dir * h });
    setTimeout(() => {
      // Swap index and reset transform without animating (would otherwise
      // visibly slide back from offscreen to center).
      if (dir > 0) goPrev();
      else goNext();
      setDrag({ dx: 0, dy: 0 });
      setAnimState("snap-skip");
      requestAnimationFrame(() => setAnimState("idle"));
    }, ANIM_MS);
  };

  const handleDelete = () => {
    if (!onDelete) return;
    if (window.confirm("Delete this image?")) {
      onDelete(item);
      if (items.length <= 1) onClose();
      else if (index >= items.length - 1) setIndex(items.length - 2);
    }
  };

  if (!item) return null;

  const isAnimating = animState === "snapping" || animState === "committing";
  const transition = isAnimating ? `transform ${ANIM_MS}ms ease-out` : "none";

  // Each layer is positioned at its "rest" offset (-w/-h, 0, +w/+h) and we
  // add the live drag delta. So the prev image follows the current up by
  // the same amount, creating the illusion that they're stacked together.
  const baseStyle = {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition,
    willChange: "transform",
  };

  const { w, h } = getSize();

  const layer = (offsetX, offsetY, src) => (
    <div
      style={{
        ...baseStyle,
        transform: `translate3d(${offsetX + drag.dx}px, ${offsetY + drag.dy}px, 0)`,
      }}
    >
      <img src={src} alt="" className="max-h-full max-w-full object-contain" draggable={false} />
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/95 backdrop-blur-sm flex flex-col"
      style={{
        overscrollBehavior: "contain",
        touchAction: "none",
        // Above the desktop sidebar's max-int z-index so phone-landscape
        // doesn't reveal the sidebar through/beside the viewer.
        zIndex: 2147483647,
      }}
    >
      <div className="flex items-center justify-between p-3 flex-shrink-0">
        <span className="text-xs text-gray-300 font-mono">
          {index + 1} / {items.length}
        </span>
        <div className="flex items-center gap-1.5">
          {onDelete && (
            <button
              onClick={handleDelete}
              className="p-2 rounded-lg text-gray-200 hover:text-red-400 hover:bg-white/[0.08] transition-colors"
              aria-label="Delete"
              title="Delete"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-100 hover:text-white hover:bg-white/[0.08] transition-colors"
            aria-label="Close"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex-1 relative min-h-0 select-none overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* Neighbors at the four cardinal off-screen positions so swiping in
            any direction reveals the right image. Only the locked-axis
            neighbor is actually visible during a drag — the other two stay
            offscreen because the drag delta on their axis is zero. */}
        {items.length > 1 && layer(-w, 0, prevItem.imageUrl)}
        {items.length > 1 && layer(w, 0, nextItem.imageUrl)}
        {items.length > 1 && layer(0, -h, prevItem.imageUrl)}
        {items.length > 1 && layer(0, h, nextItem.imageUrl)}
        {/* Current */}
        {layer(0, 0, item.imageUrl)}

        {items.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/[0.08] hover:bg-white/[0.18] text-gray-100 transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/[0.08] hover:bg-white/[0.18] text-gray-100 transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
